import { logger } from '~/utils/logger';
import { withResolvers } from '~/utils/promises';

export interface LogEvents {
  onLog(data: LineMessage, close: () => void): void | Promise<void>;
}

export async function watchBuildLogsFromNetlify(params: BuildLogsPayload, events: LogEvents) {
  const { promise, reject, resolve } = withResolvers<void>();

  watchLogs('build/logs', params, {
    async onLog(data, close) {
      try {
        await events.onLog(data, close);
      } catch (error) {
        reject(error);

        close();
      }
    },
    onPermissionError() {
      reject(new Error('Netlify Logs: Permission error'));
    },
    onFinish() {
      resolve();
    },
  });

  return promise;
}

interface NetlifyLogsPayload {
  'build/logs': BuildLogsPayload;
  'function/logs': FunctionLogsPayload;
  'edge-functions/logs': EdgeFunctionsLogsPayload;
}

interface BuildLogsPayload {
  access_token: string;
  deploy_id: string;
  site_id: string;
}

interface FunctionLogsPayload {
  access_token: string;
  account_id: string;
  function_id: string;
  site_id: string;
  from?: string;
  to?: string;
}

interface EdgeFunctionsLogsPayload {
  access_token: string;
  deploy_id: string;
  site_id: string;
  since: string;
}

interface WebSocketBaseMessage {
  ts: number;
  message: string;
}

interface LineMessage extends WebSocketBaseMessage {
  type: 'line';
  section: string;
}

interface ErrorMessage extends WebSocketBaseMessage {
  type: 'error';
  status: number;
}

interface ReportMessage extends WebSocketBaseMessage {
  type: 'report';
  section: string;
}

interface Callbacks extends LogEvents {
  onFinish(): void;
  onPermissionError(): void;
}

type WebSocketLogMessage = LineMessage | ErrorMessage | ReportMessage;

function watchLogs<K extends keyof NetlifyLogsPayload>(
  path: K,
  payload: NetlifyLogsPayload[K],
  { onFinish, onLog, onPermissionError }: Callbacks,
) {
  const websocket = new WebSocket(`wss://socketeer.services.netlify.com/${path}`);

  websocket.addEventListener('open', () => {
    websocket.send(JSON.stringify(payload));
  });

  websocket.addEventListener('message', (event) => {
    try {
      const data = JSON.parse(event.data) as WebSocketLogMessage;

      if (data.type === 'error') {
        logger.error('Netlify websocket error', data.status, data.message);

        if (data.status === 401) {
          websocket.close();

          onPermissionError();
        }

        return;
      }

      if (data.type === 'report') {
        websocket.close();

        onFinish();

        return;
      }

      onLog(data, () => {
        onFinish();
        websocket.close();
      });
    } catch (error) {
      // error could be thrown either by onLog or JSON.parse
      logger.error('Unexpected error', error);
    }
  });

  // we don't even retry for now and close the stream
  websocket.addEventListener('close', () => {
    onFinish();
  });

  websocket.addEventListener('error', (error) => {
    onFinish();

    logger.error('WebSocket error event', error);
  });
}
